/* ==========================================================================
   Aura — "Drop a hint" (PDP)

   Ring Concierge anatomy: a split overlay. Left is a live preview of the email
   the receiver will get, right is the form. The preview is the point — it shows
   the sender exactly what is about to land in someone else's inbox, which is
   what makes a stranger-emails-stranger form feel safe to submit.

   Two hard constraints from this theme, both learned the expensive way:

   1. product-info.js REPLACES everything inside <product-info> on every variant
      change. So the trigger is bound by DELEGATION from document, never to the
      link itself, and the dialog markup is rendered OUTSIDE <product-info> so a
      variant switch mid-typing cannot wipe a half-filled form.

   2. Nouk + app CSS load after ours and win on last-one-wins, so this uses a
      native <dialog>. The top layer is immune to the z-index and specificity
      fights documented in aura-rebuild-status.

   Delivery is a section setting, because the two channels answer different
   questions and Aura may only have one of them wired:

     klaviyo  POST to Klaviyo's client events API. The RECEIVER becomes the
              profile, so a Klaviyo flow triggered on the metric emails them.
              This is the only channel that actually reaches the receiver.
     contact  Native Shopify {% form 'contact' %} POST. Lands in Aura's
              notification inbox. Works on day one with nothing configured.
     both     Default. Aura gets the record immediately; the receiver gets the
              branded email the moment the flow exists.

   NOTE ON CONSENT: the client events API creates a profile, it does NOT
   subscribe anyone to marketing. Do not add a subscribe call here. The receiver
   never asked to hear from Aura and the form says so in as many words.
   ========================================================================== */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* Klaviyo's public key is already on the page — the onsite embed is installed
     store-wide. Reading it off the script tag beats a setting nobody remembers
     to fill in, but the setting still wins if it is set. */
  function klaviyoKey(dialog) {
    var explicit = dialog.getAttribute('data-klaviyo-key');
    if (explicit) return explicit;
    var tag = document.querySelector('script[src*="klaviyo.com/onsite/js/"]');
    if (!tag) return '';
    var m = tag.src.match(/company_id=([A-Za-z0-9]+)/) || tag.src.match(/onsite\/js\/([A-Za-z0-9]+)\//);
    return m ? m[1] : '';
  }

  function parseJSON(el) {
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  function setup(dialog) {
    if (dialog.dataset.auraHintBound === '1') return;
    dialog.dataset.auraHintBound = '1';

    var scopeId = dialog.getAttribute('data-scope');
    var scope = (scopeId && document.getElementById(scopeId)) || document;
    var variants = parseJSON(dialog.querySelector('[data-aura-hint-variants]'));
    var form = dialog.querySelector('[data-aura-hint-form]');
    var panel = dialog.querySelector('.aura-hint__panel');
    var mode = dialog.getAttribute('data-delivery') || 'both';

    /* Bail rather than half-bind. A dialog missing its form or panel means the
       Liquid was edited into an inconsistent state, and a trigger that opens an
       empty overlay is worse than a trigger that does nothing. */
    if (!form || !panel) return;

    var els = {
      senderName: form.querySelector('[name="hint[sender_name]"]'),
      senderEmail: form.querySelector('[name="hint[sender_email]"]'),
      toName: form.querySelector('[name="hint[to_name]"]'),
      toEmail: form.querySelector('[name="hint[to_email]"]'),
      message: form.querySelector('[name="hint[message]"]'),
      submit: form.querySelector('[data-aura-hint-submit]'),
      error: dialog.querySelector('[data-aura-hint-error]')
    };

    var preview = {
      img: dialog.querySelector('[data-aura-hint-img]'),
      title: dialog.querySelector('[data-aura-hint-title]'),
      spec: dialog.querySelector('[data-aura-hint-spec]'),
      price: dialog.querySelector('[data-aura-hint-price]'),
      from: dialog.querySelector('[data-aura-hint-from]'),
      greeting: dialog.querySelector('[data-aura-hint-greeting]'),
      note: dialog.querySelector('[data-aura-hint-note]')
    };

    /* The dialog sits outside <product-info>, so it does not get re-rendered on a
       variant change and cannot be kept in sync by the server. Read the selected
       variant at OPEN time instead — the buy form's id input is the one thing
       product-info.js is guaranteed to keep correct. */
    function currentVariant() {
      var input = scope.querySelector('form[action*="/cart/add"] input[name="id"]');
      var id = input && input.value;
      if (id && variants[id]) return variants[id];
      var first = Object.keys(variants)[0];
      return first ? variants[first] : null;
    }

    function paintProduct() {
      var v = currentVariant();
      if (!v) return;
      if (preview.img && v.image) {
        preview.img.src = v.image;
        preview.img.alt = dialog.getAttribute('data-product-title') || '';
      }
      if (preview.title) preview.title.textContent = dialog.getAttribute('data-product-title') || '';
      if (preview.spec) preview.spec.textContent = v.spec || '';
      if (preview.price) preview.price.textContent = v.price || '';
      dialog.dataset.variantId = v.id;
    }

    /* Live preview. Watching the email assemble itself as you type is the whole
       reason the left panel exists, so this runs on input, not on blur. */
    function paintPeople() {
      var from = (els.senderName.value || '').trim();
      var to = (els.toName.value || '').trim();
      if (preview.greeting) {
        preview.greeting.textContent = to
          ? dialog.getAttribute('data-greeting') + ' ' + to + ','
          : dialog.getAttribute('data-greeting') + ',';
      }
      if (preview.from) {
        preview.from.textContent = from || dialog.getAttribute('data-someone');
      }
      if (preview.note) {
        var msg = (els.message.value || '').trim();
        preview.note.textContent = msg;
        preview.note.hidden = !msg;
      }
    }

    function fail(text) {
      if (!els.error) return;
      els.error.textContent = text;
      els.error.hidden = false;
    }

    function clearFail() {
      if (els.error) els.error.hidden = true;
    }

    function open(trigger) {
      clearFail();
      panel.dataset.state = 'form';
      paintProduct();
      paintPeople();
      dialog.returnFocusTo = trigger || null;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('aura-hint-open');
      var first = form.querySelector('input, textarea');
      if (first) first.focus();
    }

    function close() {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    }

    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('aura-hint-open');
      if (dialog.returnFocusTo && document.contains(dialog.returnFocusTo)) {
        dialog.returnFocusTo.focus();
      }
    });

    /* Clicking the backdrop closes. <dialog> reports backdrop clicks as clicks on
       the dialog element itself, so compare against the panel's box rather than
       trusting the target. */
    dialog.addEventListener('click', function (e) {
      if (e.target.closest('[data-aura-hint-close]')) { close(); return; }
      if (e.target !== dialog) return;
      var r = panel.getBoundingClientRect();
      var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) close();
    });

    ['input', 'change'].forEach(function (evt) {
      form.addEventListener(evt, paintPeople);
    });

    /* ---------------- delivery ---------------- */

    function sendKlaviyo(payload) {
      var key = klaviyoKey(dialog);
      if (!key) return Promise.reject(new Error('no-klaviyo-key'));
      return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              metric: { data: { type: 'metric', attributes: { name: dialog.getAttribute('data-metric') || 'Dropped a Hint' } } },
              /* The RECEIVER is the profile. That is what makes the flow email
                 them rather than the sender. */
              profile: { data: { type: 'profile', attributes: { email: payload.toEmail, first_name: payload.toName } } },
              properties: payload.properties
            }
          }
        })
      }).then(function (r) {
        if (!r.ok && r.status !== 202) throw new Error('klaviyo-' + r.status);
        return true;
      });
    }

    function sendContact(payload) {
      var fd = new FormData();
      fd.append('form_type', 'contact');
      fd.append('utf8', '✓');
      fd.append('contact[name]', payload.senderName);
      fd.append('contact[email]', payload.senderEmail);
      fd.append('contact[Topic]', dialog.getAttribute('data-topic') || 'Drop a hint');
      fd.append('contact[body]', payload.body);
      return fetch('/contact', { method: 'POST', body: fd }).then(function (r) {
        if (!r.ok) throw new Error('contact-' + r.status);
        return true;
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearFail();
      if (!form.reportValidity()) return;

      var v = currentVariant() || {};
      var payload = {
        senderName: els.senderName.value.trim(),
        senderEmail: els.senderEmail.value.trim(),
        toName: els.toName.value.trim(),
        toEmail: els.toEmail.value.trim(),
        message: els.message.value.trim()
      };

      if (payload.toEmail.toLowerCase() === payload.senderEmail.toLowerCase()) {
        fail(dialog.getAttribute('data-err-same'));
        els.toEmail.focus();
        return;
      }

      payload.properties = {
        SenderName: payload.senderName,
        SenderEmail: payload.senderEmail,
        ReceiverName: payload.toName,
        Message: payload.message,
        ProductTitle: dialog.getAttribute('data-product-title'),
        ProductURL: dialog.getAttribute('data-product-url'),
        ProductImage: v.image || '',
        VariantTitle: v.spec || '',
        VariantID: v.id || '',
        Price: v.price || ''
      };

      payload.body = [
        payload.senderName + ' dropped a hint.',
        '',
        'To:      ' + payload.toName + ' <' + payload.toEmail + '>',
        'From:    ' + payload.senderName + ' <' + payload.senderEmail + '>',
        'Product: ' + payload.properties.ProductTitle + ' (' + (v.spec || '') + ') ' + (v.price || ''),
        'Link:    ' + payload.properties.ProductURL,
        '',
        'Message: ' + (payload.message || '(none)')
      ].join('\n');

      panel.dataset.state = 'sending';
      els.submit.disabled = true;

      var jobs = [];
      if (mode === 'klaviyo' || mode === 'both') jobs.push(sendKlaviyo(payload));
      if (mode === 'contact' || mode === 'both') jobs.push(sendContact(payload));
      if (!jobs.length) jobs.push(Promise.reject(new Error('no-channel')));

      /* allSettled, not all: with both channels on, one landing is a delivered
         hint. Only a clean sweep of failures is a failure. */
      Promise.allSettled(jobs).then(function (results) {
        var any = results.some(function (r) { return r.status === 'fulfilled'; });
        els.submit.disabled = false;
        if (!any) {
          panel.dataset.state = 'form';
          fail(dialog.getAttribute('data-err-send'));
          results.forEach(function (r) {
            if (r.status === 'rejected') console.warn('[aura-hint]', r.reason && r.reason.message);
          });
          return;
        }
        panel.dataset.state = 'sent';
        var sentName = dialog.querySelector('[data-aura-hint-sentname]');
        if (sentName) sentName.textContent = payload.toName;
        form.reset();
      });
    });

    /* Trigger is delegated from document: it lives inside <product-info> and is
       destroyed and rebuilt on every variant change. */
    document.addEventListener('click', function (e) {
      var t = e.target.closest('[data-aura-hint-open]');
      if (!t) return;
      /* Re-resolved on every click, not cached: the scope root survives variant
         swaps but the trigger inside it does not. */
      var root = scopeId ? document.getElementById(scopeId) : null;
      if (root && !root.contains(t)) return;
      e.preventDefault();
      open(t);
    });
  }

  ready(function () {
    document.querySelectorAll('[data-aura-hint]').forEach(setup);
  });
})();
